import React from "react";

import {
  publicationStatusLabel,
  publicationStatusTone,
  statusReason,
} from "@/components/portfolio/revision-presentation";
import { EFFECTIVE_DATE_HELP } from "@/components/shared/InitialApprovalFields";
import {
  Badge,
  Button,
  Combobox,
  countLabel,
  Heading,
  Icon,
  MutedText,
  Text,
  Toggle,
} from "@/components/ui";
import { FormField, TextField } from "@/components/ui/Form";
import type { ProgrammeGovernanceSummary, RevisionPublicationEntry } from "@/platform";

/** The status badge, which sits at the end of its row so every row reads the same way. */
function PublicationBadge({
  entry,
}: {
  entry: RevisionPublicationEntry | undefined;
}): React.ReactElement | null {
  if (!entry) {
    return null;
  }
  return (
    <Badge
      tone={publicationStatusTone(entry.status)}
      variant="soft"
      size="sm"
      className="shrink-0"
      data-testid="applicability-status"
    >
      {publicationStatusLabel(entry.status)}
    </Badge>
  );
}

export interface RevisionApplicabilityProps {
  governance: ProgrammeGovernanceSummary;
  approvedRevision: ProgrammeGovernanceSummary["governanceRevisions"][number] | undefined;
  canSetApplicability: boolean;
  applicabilityRevisionId: string;
  effectiveFrom: string;
  onEffectiveFromChange: (value: string) => void;
  publicationConsent: boolean;
  onPublicationConsentChange: (value: boolean) => void;
  onSelectApplicabilityRevision: (governanceRevisionId: string) => void;
  onAddApplicability: () => Promise<void>;
  actionPending: boolean;
}

export function RevisionApplicability({
  governance,
  approvedRevision,
  canSetApplicability,
  applicabilityRevisionId,
  effectiveFrom,
  onEffectiveFromChange,
  publicationConsent,
  onPublicationConsentChange,
  onSelectApplicabilityRevision,
  onAddApplicability,
  actionPending,
}: RevisionApplicabilityProps): React.ReactElement | null {
  if (!approvedRevision) {
    return null;
  }
  const publicationOf = (applicabilityId: string) =>
    governance.revisionPublication.find((entry) => entry.applicabilityId === applicabilityId);

  return (
    <section className="mt-4" data-testid="revision-applicability">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading level={3} size="md">
            Effective dates
          </Heading>
          <Text tone="muted" className="mt-1">
            {countLabel(governance.revisionApplicability.length, "applicability rule")} recorded.
          </Text>
        </div>
      </div>
      {governance.revisionApplicability.length ? (
        <ul className="mt-3 space-y-2 text-md" data-testid="applicability-rules">
          {governance.revisionApplicability.map((rule) => {
            const entry = publicationOf(rule.id);
            return (
              <li key={rule.id}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="font-medium">From {rule.effectiveFrom}</span>
                  </div>
                  <PublicationBadge entry={entry} />
                </div>
                {entry ? (
                  <MutedText size="caption" className="mt-1">
                    {statusReason(entry)}
                  </MutedText>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {canSetApplicability ? (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <FormField label="Approved revision" htmlFor="applicability-revision">
            <Combobox
              id="applicability-revision"
              value={applicabilityRevisionId || approvedRevision.id}
              onChange={onSelectApplicabilityRevision}
              options={governance.governanceRevisions
                .filter((revision) => revision.kind === "approved")
                .map((revision) => {
                  const curriculum = governance.curriculumRevisions.find(
                    (entry) => entry.id === revision.id,
                  );
                  return {
                    value: revision.id,
                    label: curriculum?.institutionalLabel ?? `Revision ${revision.revisionNumber}`,
                  };
                })}
              emptyMessage="No approved revisions match"
              disabled={actionPending}
              disabledReason="Wait for the action in progress to finish."
              data-testid="applicability-revision"
            />
          </FormField>
          <FormField
            label="Effective from"
            htmlFor="applicability-effective-from"
            helpText={EFFECTIVE_DATE_HELP}
          >
            <TextField
              id="applicability-effective-from"
              type="date"
              value={effectiveFrom}
              onChange={onEffectiveFromChange}
              disabled={actionPending}
              disabledReason="Wait for the action in progress to finish."
              data-testid="applicability-effective-from"
            />
          </FormField>
          <div>
            <Toggle
              label="Consent to publish to integrations"
              checked={publicationConsent}
              onChange={onPublicationConsentChange}
              disabled={actionPending}
              disabledReason="Wait for the action in progress to finish."
              data-testid="applicability-publication-consent"
            />
          </div>
          <div className="flex items-center md:justify-end">
            <Button
              size="sm"
              variant="outline"
              type="button"
              disabled={actionPending || !effectiveFrom}
              disabledReason={
                actionPending
                  ? "Wait for the action in progress to finish."
                  : (!effectiveFrom && "Choose the date it takes effect first.") || undefined
              }
              onClick={() => onAddApplicability()}
              data-testid="add-applicability"
            >
              <Icon name="calendar-plus" aria-hidden /> Record applicability
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
